import Decimal from 'decimal.js';
import {
  DECIMAL_CONVENTION,
  localeForConvention,
  getDecimalConvention,
  getOrgDefaultConvention,
  useDecimalConventionStore,
} from '../stores/useDecimalConventionStore';
import { useLocaleStore } from '../stores/useLocaleStore';
import { getRegimeCurrency } from '../stores/useRegimeCurrencyStore';
import { numberFormatter } from './intl';

// A Mongo Decimal128 arrives as `{ "$numberDecimal": "6.54E-7" }`: the string is
// the value. Anything else is returned as it came.
export const unwrapDecimal128 = (value) =>
  value !== null && typeof value === 'object' && typeof value.$numberDecimal === 'string'
    ? value.$numberDecimal
    : value;

const toNumber = (raw) => {
  const value = unwrapDecimal128(raw);
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'object' && typeof value.toNumber === 'function') {
    const n = value.toNumber();
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// A non-zero value must never render as zero. NCVs for gases (TJ/m³) sit
// around 0.00003, so a fixed 4-decimal budget printed them as "0.0000" and a
// verifier read the calculation as wrong. When the decimal budget would round
// a value's first significant digit away, show SMALL_VALUE_SIGNIFICANT_DIGITS
// significant figures instead (0.0000267), and below SCIENTIFIC_BELOW switch
// to scientific notation (2.67E-7) so the zeros stay countable. Display only:
// stored values and the maths are untouched.
const SMALL_VALUE_SIGNIFICANT_DIGITS = 3;
const SCIENTIFIC_BELOW = 1e-6;
// Below this a value is float residue (0.1 + 0.2 − 0.3 = 5.55e-17), not data:
// it keeps rendering as zero. The smallest real factor (BFG NCV) is 3e-6.
const FLOAT_RESIDUE = 1e-12;

// Would rounding to `decimals` places turn this non-zero value into zero?
const losesSignificance = (n, decimals) =>
  Number.isFinite(n) && Math.abs(n) >= FLOAT_RESIDUE && Math.abs(n) < 0.5 * 10 ** -decimals;

// Intl options for a value `losesSignificance` flagged. Plain vs scientific is
// decided on the unrounded value, exactly as `smallValueString` decides it.
const smallValueOptions = (n) => ({
  maximumSignificantDigits: SMALL_VALUE_SIGNIFICANT_DIGITS,
  ...(Math.abs(n) < SCIENTIFIC_BELOW ? { notation: 'scientific' } : {}),
});

// 3 significant figures, from the Decimal itself so nothing underflows.
const smallValueString = (d) => {
  const rounded = d.toSignificantDigits(SMALL_VALUE_SIGNIFICANT_DIGITS);
  return d.abs().lt(SCIENTIFIC_BELOW) ? rounded.toExponential().toUpperCase() : rounded.toFixed();
};

// Locale-neutral twin of `.toFixed(decimals)` for the places that need a plain
// machine string (LaTeX, CSV, text the PDF builds by hand) and so cannot use
// `formatNumber`. Same small-value rule; "2.67E-7" matches Intl's notation.
// Otherwise it is exactly the `toFixed` the caller had: Decimal's (half-up)
// for a Decimal, Number's for anything else — so pass a Decimal where the
// caller relied on Decimal rounding.
export const toFixedNonZero = (value, decimals) => {
  if (Decimal.isDecimal(value)) {
    // Exponent test first: the common path allocates nothing new.
    if (value.e >= -decimals || !losesSignificance(value.toNumber(), decimals)) {
      return value.toFixed(decimals);
    }
    return smallValueString(value);
  }
  const n = toNumber(value);
  if (n === null) return '';
  return losesSignificance(n, decimals) ? smallValueString(new Decimal(n)) : n.toFixed(decimals);
};

// `toFixedNonZero` as a number, for numeric spreadsheet cells and other values
// rounded for display that must stay numbers.
export const roundNonZero = (value, decimals) => {
  const fixed = toFixedNonZero(value, decimals);
  return fixed === '' ? null : Number(fixed);
};

// Resolve the BCP-47 tag for a formatting call. `localeCode` lets a caller pin
// the language explicitly — phase 7's export generators must, because an
// artifact's language is the recipient's, not the logged-in user's.
const tagFor = (convention, localeCode) =>
  localeForConvention(convention ?? getDecimalConvention(), localeCode, getOrgDefaultConvention());

// Format a number using the given convention. Convention defaults to the
// active store value, so callers outside React (PDF/Excel/data prep) get
// the user's chosen format without threading it through.
//
// `useGrouping: true` is not a stylistic default — leaving it unset falls
// through to Intl's own default, `'auto'`, which drops the thousands
// separator entirely for es-ES/it-IT/pl-PL in this app's ICU build (2314.82
// renders "2314,82" instead of "2.314,82"). de-DE/fr-FR/cs-CZ/el-GR/en-GB are
// unaffected — this is a per-locale ICU quirk with `'auto'`, not a
// misconfiguration here, and `'always'` sidesteps it identically everywhere
// (verified: no spurious separator below 1000, correct grouping above it, in
// every shipped locale). See numberFormat.test.js.
export const formatNumber = (
  value,
  {
    convention,
    localeCode,
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    useGrouping = true,
    ...rest
  } = {},
) => {
  const n = toNumber(value);
  if (n === null) return '';
  // A caller that picked significant digits or a notation itself keeps them.
  const keepsOwnPrecision =
    rest.maximumSignificantDigits !== undefined || rest.notation !== undefined;
  const digits =
    !keepsOwnPrecision && losesSignificance(n, maximumFractionDigits)
      ? smallValueOptions(n)
      : { minimumFractionDigits, maximumFractionDigits };
  return numberFormatter(tagFor(convention, localeCode), {
    ...digits,
    useGrouping,
    ...rest,
  }).format(n);
};

// A mass of goods: every place it has, up to MASS_DECIMALS, no trailing zeros
// (`0.000654`, `0.000000654`, `42`). Net mass is declared in kg to HMRC's 6
// places, which is 9 in tonnes — so a 654 mg package is never a "0.0 t"
// (docs/features/uk-kg-precision D4).
const MASS_DECIMALS = 9;

export const formatMass = (value, options = {}) =>
  formatNumber(value, { maximumFractionDigits: MASS_DECIMALS, ...options });

// Drop-in replacement for `value.toFixed(N)` that respects the user's
// decimal convention. Always renders N decimals (matching toFixed) and
// includes thousands grouping by default. Returns '' for null/NaN where
// `.toFixed` would throw — callers that previously got "NaN" should
// short-circuit upstream if they need a placeholder like "-".
export const formatDecimal = (value, decimals = 2, { convention, localeCode, ...rest } = {}) =>
  formatNumber(value, {
    convention,
    localeCode,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    ...rest,
  });

// `currency` defaults to the REGIME's currency rather than to a literal, so a
// UK CBAM declarant's money reads in sterling wherever an EU declarant's reads
// in euros — including from the Excel and PDF generators, which are plain
// modules and cannot call a hook. `getRegimeCurrency()` answers `'EUR'` until
// the server has said otherwise, so every EU call site — and every non-
// declarant surface (public catalog, operator, consultant, lite supplier) —
// renders exactly what it rendered when this default was the literal 'EUR'.
// A caller that knows its currency should still pass it; explicit wins.
export const formatCurrency = (
  value,
  {
    convention,
    localeCode,
    currency = getRegimeCurrency(),
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    // See the comment on formatNumber — `'auto'` (Intl's default) drops the
    // thousands separator for es-ES/it-IT/pl-PL specifically.
    useGrouping = true,
    ...rest
  } = {},
) => {
  const n = toNumber(value);
  if (n === null) return '';
  return numberFormatter(tagFor(convention, localeCode), {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping,
    ...rest,
  }).format(n);
};

// The bare glyph, for the places a symbol appears WITHOUT a number beside it:
// a chart's axis title, a unit chip, a subtitle. Defaults to the regime's
// currency for the same reason `formatCurrency` does, so a UK declarant's
// "£/t" column reads as an EU declarant's "€/t" does.
//
// A map rather than `Intl`'s `formatToParts`: only two currencies exist here,
// and the glyph MUST match what `formatCurrency` prints beside it — deriving
// it a second, different way is how a header and its cells come to disagree.
export const currencySymbol = (currency = getRegimeCurrency()) =>
  ({ EUR: '€', GBP: '£' })[currency] ?? currency;

// Percentages. `value` is the PERCENTAGE, not the ratio — formatPercent(12.5)
// renders "12.5%" — because that is how every call site in this codebase
// already holds the number.
//
// Not `${formatNumber(v)}%`: Spanish and Italian put a non-breaking space
// before the sign (12,5 %) and English does not (12.5%). Hand-concatenating is
// wrong in two of our three launch languages.
export const formatPercent = (
  value,
  {
    convention,
    localeCode,
    minimumFractionDigits = 0,
    maximumFractionDigits = 1,
    // See the comment on formatNumber — same `'auto'` grouping bug applies
    // here for any percentage large enough to need it (e.g. cost variance).
    useGrouping = true,
    ...rest
  } = {},
) => {
  const n = toNumber(value);
  if (n === null) return '';
  return numberFormatter(tagFor(convention, localeCode), {
    style: 'percent',
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping,
    ...rest,
  }).format(n / 100);
};

// Unit-bearing quantities. The unit SYMBOL is never translated (tCO2e, MWh, t
// are regulatory notation) but the number beside it is localized:
//   en  1,234.56 tCO2e
//   es  1.234,56 tCO2e
export const formatQuantity = (value, unit, options) => {
  const formatted = formatNumber(value, options);
  if (formatted === '') return '';
  return unit ? `${formatted} ${unit}` : formatted;
};

// Does this convention render a comma as the DECIMAL separator? Derived from
// Intl rather than a hardcoded list, so it stays correct for a locale added
// later without touching this file.
export const usesCommaDecimal = (convention = getDecimalConvention(), localeCode) =>
  numberFormatter(tagFor(convention, localeCode), {})
    .formatToParts(1.1)
    .some((part) => part.type === 'decimal' && part.value === ',');

// Convert a stored numeric value (number or numeric string) to a string
// suitable for display in a controlled `<input type="text">`. Mid-typing
// or malformed values pass through unchanged so the user's keystrokes
// aren't clobbered. Use this anywhere an editable field shows a number
// and you want European users to see "97,5" instead of "97.5".
//
// Deliberately grouping-free: inserting thousands separators mid-edit fights
// the caret.
export const formatInputDisplay = (value, convention = getDecimalConvention()) => {
  if (value === null || value === undefined || value === '') return '';
  const str = String(value);
  if (!/^-?\d*\.?\d*$/.test(str)) return str;
  return usesCommaDecimal(convention) ? str.replace('.', ',') : str;
};

// The mirror image of formatInputDisplay: turn what the user typed back into a
// JS number for submission.
//
// This is the highest-consequence function in the phase. Without it a Spanish
// user typing "97,5" into an emissions field submits 97 or NaN — silently, and
// into a CBAM filing. Returns null (never NaN) for anything unparseable, so
// callers can distinguish "empty" from "zero".
export const parseLocaleNumber = (input, convention = getDecimalConvention(), localeCode) => {
  if (input === null || input === undefined) return null;
  if (typeof input === 'number') return Number.isFinite(input) ? input : null;

  let str = String(input).trim();
  if (str === '') return null;
  // Strip spaces used as group separators (fr-style, and what users paste out
  // of Excel), including the non-breaking and narrow variants Intl emits.
  str = str.replace(/[\s\u00a0\u202f]/g, '');

  const commaIsDecimal = usesCommaDecimal(convention, localeCode);
  const decimalMark = commaIsDecimal ? ',' : '.';
  const groupMark = commaIsDecimal ? '.' : ',';

  const sign = str.startsWith('-') ? -1 : 1;
  str = str.replace(/^[+-]/, '');

  const decimalCount = str.split(decimalMark).length - 1;
  if (decimalCount > 1) return null; // "1,2,3" — no reading of this is valid

  const [wholeRaw, fraction = ''] = str.split(decimalMark);

  // Validate the group separators STRUCTURALLY rather than stripping them
  // blindly. Blind stripping turns "1234.56" into 123456 for a European user —
  // a silent 100x error on a number that ends up in a CBAM filing. Real
  // grouping always has 1-3 digits in the first group and exactly 3 in every
  // group after it.
  let whole = wholeRaw;
  if (whole.includes(groupMark)) {
    const groups = whole.split(groupMark);
    const wellFormed =
      /^\d{1,3}$/.test(groups[0]) && groups.slice(1).every((g) => /^\d{3}$/.test(g));

    if (wellFormed) {
      whole = groups.join('');
    } else if (groups.length === 2 && decimalCount === 0) {
      // A single separator that cannot be grouping, with no decimal mark in
      // sight: the user typed the OTHER convention's decimal point, which is a
      // very common habit. Reading it as a decimal is what they meant.
      const candidate = `${groups[0]}.${groups[1]}`;
      if (!/^\d+\.\d+$/.test(candidate)) return null;
      return sign * Number(candidate);
    } else {
      return null;
    }
  }

  const normalised = `${whole}.${fraction || '0'}`;
  if (!/^\d+\.\d+$/.test(normalised)) return null;
  const n = Number(normalised);
  return Number.isFinite(n) ? sign * n : null;
};

// React hook that returns formatters bound to the current convention AND the
// current language. Subscribing to both stores is what makes a component
// re-render when the user switches language, not only when they change the
// number format.
export const useNumberFormatter = () => {
  const convention = useDecimalConventionStore((s) => s.convention) ?? DECIMAL_CONVENTION.AUTO;
  // Subscribed for its re-render side effect: AUTO's output depends on it.
  const localeCode = useLocaleStore((s) => s.locale);

  return {
    convention,
    localeCode,
    formatNumber: (value, options) => formatNumber(value, { ...options, convention }),
    formatDecimal: (value, decimals, options) =>
      formatDecimal(value, decimals, { ...options, convention }),
    formatCurrency: (value, options) => formatCurrency(value, { ...options, convention }),
    formatPercent: (value, options) => formatPercent(value, { ...options, convention }),
    formatQuantity: (value, unit, options) =>
      formatQuantity(value, unit, { ...options, convention }),
    formatInputDisplay: (value) => formatInputDisplay(value, convention),
    parseLocaleNumber: (value) => parseLocaleNumber(value, convention),
  };
};
